import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Fuse from 'fuse.js';
import { ChangeEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useOnClickOutside } from 'usehooks-ts';

import { InputFieldStyles } from '../InputField';

interface Option {
  label: string;
  value: string;
}

interface Props {
  options: Option[];
  label?: ReactNode;
  searchLabel?: string;
  values?: string[];
  onChange: (values: string[]) => void;
  selectLabel?: string;
  className?: string;
  dropdownClassName?: string;
  variant?: 'light' | 'dark' | 'transparent';
  error?: boolean;
}

export const MultiSearchDropdown = ({
  options,
  label,
  searchLabel,
  values = [],
  onChange: handleChange,
  selectLabel,
  className,
  dropdownClassName,
  variant = 'light',
  error,
}: Props) => {
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(wrapperRef, () => setIsOpen(false));

  const fuse = useMemo(() => {
    const _options = {
      keys: ['label'],
    };

    const index = Fuse.createIndex(_options.keys, options);

    return new Fuse(options, _options, index);
  }, [options]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const results = fuse.search(searchTerm).map(result => result.item);

      setFilteredOptions(searchTerm ? results : options);
    }, 100);

    return () => clearTimeout(timer);
  }, [searchTerm, options, fuse]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleToggleOption = (option: Option) => {
    const isSelected = values.includes(option.value);

    if (isSelected) {
      handleChange(values.filter(v => v !== option.value));
    } else {
      handleChange([...values, option.value]);
    }
  };

  const toggleDropdown = () => {
    setIsOpen(prevState => !prevState);
  };

  const handleOnEnter = (e: React.KeyboardEvent<HTMLLIElement>, option: Option) => {
    if (e.key !== 'Enter') {
      return;
    }

    handleToggleOption(option);
  };

  return (
    <div className={twMerge('relative', className)} ref={wrapperRef}>
      <div
        className={twMerge(
          'relative min-h-10 cursor-pointer appearance-none bg-none',
          InputFieldStyles({
            variant,
            error: !!error,
          }),
          'pr-10',
          dropdownClassName,
        )}
        data-testid="stores-dropdown"
        onClick={toggleDropdown}
      >
        <div className="flex flex-1 flex-wrap gap-2">
          <span className="text-gray-dark">{label ?? selectLabel ?? 'Select options'}</span>
        </div>
        <FontAwesomeIcon
          className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2"
          icon={faChevronDown}
        />
      </div>
      {isOpen && (
        <div className="border-1 border-gray-light rounded-brand-xs absolute left-0 top-full z-50 mt-1 max-h-80 w-full overflow-y-auto bg-white shadow-lg">
          {options.length > 1 && (
            <input
              className="border-gray-light w-full border-b bg-white p-2 italic focus:outline-none"
              onChange={handleInputChange}
              placeholder={searchLabel ?? 'Search...'}
              type="text"
              value={searchTerm}
            />
          )}
          <ul className="max-h-60 overflow-y-auto">
            {filteredOptions.map(option => {
              const isSelected = values.includes(option.value);

              return (
                <li
                  className={twMerge(
                    'cursor-pointer p-2 hover:bg-gray-100 focus:bg-gray-100',
                    isSelected && 'bg-blue-100 font-semibold',
                  )}
                  key={option.value}
                  onClick={() => handleToggleOption(option)}
                  onKeyDown={e => handleOnEnter(e, option)}
                  tabIndex={0}
                >
                  <input checked={isSelected} className="mr-2" readOnly type="checkbox" />
                  {option.label}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MultiSearchDropdown;
